import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Bike,
  MapPin,
  Phone,
  MessageCircle,
  Navigation,
  Package,
  Clock,
  CheckCircle2,
  RefreshCw,
  LogOut,
  Search,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  User,
  ShieldCheck,
  Store,
  ArrowRight,
  Check,
  Wallet,
  Banknote,
  Bell,
  Volume2,
  VolumeX,
  X,
  Trash2,
  CheckCheck,
  Sparkles,
  ExternalLink,
  Radio,
  Smartphone,
  BellRing,
  XCircle,
} from 'lucide-react';
import { useContent } from '../../context/ContentContext';
import { CustomerOrder, CourierWalletSummary } from '../../types';
import { resolvePersistedOrderServiceFee } from '../../utils/deliveryFee';
import {
  fetchCourierDeliveryOrders,
  performCourierOrderAction,
  CourierActionType,
} from '../../utils/supabaseOrders';
import { fetchCourierWalletDashboard } from '../../utils/courierWallet';
import { CourierWalletView } from './CourierWalletView';
import { supabase } from '../../utils/supabase';
import {
  checkPushNotificationSupport,
  subscribeCourierPushNotification,
  unsubscribeCourierPushNotification,
} from '../../utils/pushNotification';
import {
  initOneSignal,
  subscribeOneSignalCourier,
  unsubscribeOneSignalCourier,
  setupOneSignalAutoSync,
  normalizeOutletTag,
} from '../../utils/oneSignal';

type CourierTab = 'available' | 'my_active' | 'all_active' | 'completed' | 'wallet';

export interface CourierNotificationItem {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerPhone?: string;
  outletName: string;
  outletId: string;
  deliveryAddress?: string;
  deliveryDistance?: number;
  deliveryFee?: number;
  totalAmount: number;
  orderStatus: string;
  paymentStatus: string;
  createdAt: string;
  receivedAt: string;
  isRead: boolean;
}

const normalizeDeliveryStatus = (status?: string): string => {
  const s = String(status || '').trim().toUpperCase();
  if (!s || s === 'PENDING') return 'UNASSIGNED';
  return s;
};

const playDeliveryChime = (isMuted: boolean) => {
  if (isMuted) return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;

    // First Note: G5 (783.99 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(783.99, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.4, now + 0.04);
    gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);

    // Second Note: C6 (1046.50 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1046.50, now + 0.15);
    gain2.gain.setValueAtTime(0, now + 0.15);
    gain2.gain.linearRampToValueAtTime(0.5, now + 0.19);
    gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.8);
  } catch (e) {
    console.warn('[Courier Audio Chime Notice]:', e);
  }
};

const isEligibleDeliveryNotification = (
  order: any,
  courierOutletId?: string
): boolean => {
  if (!order) return false;

  const oType = String(order.order_type || order.orderType || '').toUpperCase().trim();
  if (oType !== 'DELIVERY') return false;

  const oOutlet = String(order.outlet_id || order.outletId || '').toLowerCase().trim();
  const reqOutlet = String(courierOutletId || '').toLowerCase().trim();
  if (reqOutlet && reqOutlet !== 'all' && oOutlet !== reqOutlet) {
    return false;
  }

  const oStatus = String(order.order_status || order.orderStatus || '').toUpperCase().trim();
  if (['CANCELLED', 'REJECTED'].includes(oStatus)) return false;

  const pStatus = String(order.payment_status || order.paymentStatus || '').toUpperCase().trim();
  if (['WAITING PAYMENT', 'WAITING_PAYMENT', 'PAY AT STORE'].includes(pStatus)) {
    return false;
  }

  return true;
};

const getDeliveryStatusBadge = (status?: string) => {
  const norm = normalizeDeliveryStatus(status);
  switch (norm) {
    case 'ASSIGNED':
      return {
        label: 'Kurir: Ditugaskan',
        bg: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
      };
    case 'PICKED_UP':
      return {
        label: 'Kurir: Pesanan Dijemput',
        bg: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
      };
    case 'ON_DELIVERY':
      return {
        label: 'Kurir: Sedang Diantar',
        bg: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
      };
    case 'DELIVERED':
      return {
        label: 'Kurir: Selesai Diantar',
        bg: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      };
    default:
      return {
        label: 'Kurir: Menunggu Dijemput',
        bg: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
      };
  }
};

const getKitchenStatusBadge = (orderStatus?: string) => {
  const s = String(orderStatus || 'NEW').trim().toUpperCase();
  switch (s) {
    case 'READY':
      return {
        label: 'Outlet: Siap Diserahkan',
        bg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      };
    case 'PREPARING':
      return {
        label: 'Outlet: Sedang Disiapkan',
        bg: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      };
    case 'ACCEPTED':
      return {
        label: 'Outlet: Diterima',
        bg: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      };
    case 'COMPLETED':
      return {
        label: 'Outlet: Selesai',
        bg: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      };
    case 'CANCELLED':
      return {
        label: 'Outlet: Dibatalkan',
        bg: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      };
    default:
      return {
        label: 'Outlet: Pesanan Baru',
        bg: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      };
  }
};

const formatCurrency = (val?: number) => {
  const num = Number(val || 0);
  return `Rp ${num.toLocaleString('id-ID')}`;
};

const formatPhoneForWa = (phone?: string) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('0')) return `62${digits.slice(1)}`;
  if (digits.startsWith('8')) return `62${digits}`;
  return digits;
};

export const CourierPortal: React.FC = () => {
  const { auth, logout } = useContent();
  const adminOutletId = auth.outletId;
  const adminOutletName = auth.outletName;

  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [activeTab, setActiveTab] = useState<CourierTab>('available');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedOrderIds, setExpandedOrderIds] = useState<Record<string, boolean>>({});
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);
  const [confirmCompleteOrderId, setConfirmCompleteOrderId] = useState<string | null>(null);
  const [walletSummary, setWalletSummary] = useState<CourierWalletSummary | null>(null);

  const currentUsername = (auth.username || '').trim().toLowerCase();

  // Storage Keys scoped per courier username
  const notifStorageKey = `leton_courier_notifs_${currentUsername || 'default'}`;
  const notifiedIdsStorageKey = `leton_courier_notified_${currentUsername || 'default'}`;
  const soundMutedStorageKey = `leton_courier_sound_muted`;

  const [soundMuted, setSoundMuted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(soundMutedStorageKey) === 'true';
  });

  const [notifications, setNotifications] = useState<CourierNotificationItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(notifStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [isNotifDrawerOpen, setIsNotifDrawerOpen] = useState<boolean>(false);
  const [deliveryToast, setDeliveryToast] = useState<CourierNotificationItem | null>(null);

  // Web Push State (OneSignal v16 & Native VAPID)
  const [pushStatus, setPushStatus] = useState<'active' | 'inactive' | 'denied' | 'unsupported'>('inactive');
  const [isIOSDevice, setIsIOSDevice] = useState<boolean>(false);
  const [isPWAStandalone, setIsPWAStandalone] = useState<boolean>(false);
  const [pushLoading, setPushLoading] = useState<boolean>(false);
  const [oneSignalSubId, setOneSignalSubId] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [oneSignalError, setOneSignalError] = useState<string | null>(null);

  const notifiedOrderIdsRef = useRef<Set<string>>(new Set());

  // Initialize notifiedOrderIdsRef from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(notifiedIdsStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          notifiedOrderIdsRef.current = new Set(parsed);
        }
      }
    } catch {}
  }, [notifiedIdsStorageKey]);

  // Persist notifications to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(notifStorageKey, JSON.stringify(notifications.slice(0, 50)));
    } catch {}
  }, [notifications, notifStorageKey]);

  // Check Web Push Notification status on mount
  useEffect(() => {
    const checkPush = async () => {
      const support = checkPushNotificationSupport();
      setIsIOSDevice(support.isIOS);
      setIsPWAStandalone(support.isStandalone);

      if (typeof Notification !== 'undefined') {
        setPermissionState(Notification.permission);
      } else {
        setPermissionState('unsupported');
      }

      if (!support.isSupported) {
        setPushStatus('unsupported');
        return;
      }

      if (support.permission === 'denied') {
        setPushStatus('denied');
        return;
      }

      // 1. Check OneSignal SDK status
      try {
        const oneSignal = await initOneSignal().catch(() => null);
        if (oneSignal?.User?.PushSubscription) {
          const subId = oneSignal.User.PushSubscription.id;
          const isOptedIn = Boolean(oneSignal.User.PushSubscription.optedIn);
          if (subId) {
            setOneSignalSubId(subId);
          }
          if (isOptedIn && Notification.permission === 'granted') {
            setPushStatus('active');
            void setupOneSignalAutoSync(auth.username, adminOutletId || 'sudirman', 'courier');
            return;
          }
        }
      } catch (err) {
        console.warn('[Courier OneSignal Check Notice]:', err);
      }

      // 2. Check Native VAPID subscription as secondary check
      try {
        if ('serviceWorker' in navigator) {
          let reg = await navigator.serviceWorker.getRegistration('/');
          if (!reg) {
            reg = await navigator.serviceWorker.getRegistration();
          }
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
              setPushStatus('active');
              return;
            }
          }
        }
      } catch {}

      const isSubbedLocal = localStorage.getItem('leton_courier_push_subscribed') === 'true';
      setPushStatus(isSubbedLocal ? 'active' : 'inactive');
    };

    checkPush();
  }, [adminOutletId, auth.username]);

  const toggleSoundMuted = () => {
    setSoundMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(soundMutedStorageKey, String(next));
      } catch {}
      return next;
    });
  };

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToastMsg({ text, type });
    setTimeout(() => {
      setToastMsg((prev) => (prev?.text === text ? null : prev));
    }, 4000);
  }, []);

  const handleTogglePushNotification = async () => {
    setPushLoading(true);
    setOneSignalError(null);
    try {
      if (pushStatus === 'active') {
        // Unsubscribe from OneSignal & Native VAPID
        await unsubscribeOneSignalCourier();
        await unsubscribeCourierPushNotification({
          authToken: auth.token,
        });
        setOneSignalSubId(null);
        setPushStatus('inactive');
        showToast('Notifikasi Web Push (OneSignal & VAPID) berhasil dinonaktifkan.');
      } else {
        // 1. Primary: Subscribe to OneSignal Web Push with Courier External ID & Tags
        const osRes = await subscribeOneSignalCourier(
          auth.username,
          adminOutletId || 'sudirman'
        );

        // 2. Secondary Fallback: Subscribe to Native VAPID Push
        void subscribeCourierPushNotification({
          courierUsername: auth.username,
          outletId: adminOutletId || 'sudirman',
          role: 'courier',
          authToken: auth.token,
        });

        if (osRes.success) {
          setPushStatus('active');
          if (osRes.subscriptionId) {
            setOneSignalSubId(osRes.subscriptionId);
          }
          if (typeof Notification !== 'undefined') {
            setPermissionState(Notification.permission);
          }
          showToast(`Akun Kurir (${auth.username}) berhasil terdaftar di OneSignal! Web Push aktif.`, 'success');
        } else {
          if (osRes.permission === 'denied' || Notification.permission === 'denied') {
            setPushStatus('denied');
            setPermissionState('denied');
          }
          setOneSignalError(osRes.error || 'Gagal mengaktifkan Notifikasi Web Push OneSignal.');
          showToast(osRes.error || 'Gagal mengaktifkan Notifikasi Web Push OneSignal.', 'error');
        }
      }
    } catch (err: any) {
      const msg = err?.message || 'Terjadi kesalahan sistem.';
      setOneSignalError(msg);
      showToast(msg, 'error');
    } finally {
      setPushLoading(false);
    }
  };


  const unreadNotifCount = useMemo(() => {
    return notifications.filter((n) => !n.isRead).length;
  }, [notifications]);

  const markNotifAsRead = useCallback((notifId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, isRead: true } : n))
    );
  }, []);

  const markAllNotifsAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  }, []);

  const clearAllNotifs = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleSelectNotifOrder = useCallback((notif: CourierNotificationItem) => {
    markNotifAsRead(notif.id);
    setDeliveryToast(null);
    setIsNotifDrawerOpen(false);
    setSearchQuery(notif.orderNumber);
    setActiveTab('available');
    setExpandedOrderIds((prev) => ({ ...prev, [notif.orderId]: true }));
  }, [markNotifAsRead]);

  const processNewDeliveryOrderNotification = useCallback((rawOrder: any) => {
    if (!isEligibleDeliveryNotification(rawOrder, adminOutletId)) return;

    const oId = String(rawOrder.id || '').trim();
    if (!oId) return;

    if (notifiedOrderIdsRef.current.has(oId)) return; // Idempotent check

    // Mark as notified
    notifiedOrderIdsRef.current.add(oId);
    try {
      const arr = Array.from(notifiedOrderIdsRef.current).slice(-100);
      localStorage.setItem(notifiedIdsStorageKey, JSON.stringify(arr));
    } catch {}

    const orderNum = String(rawOrder.order_number || rawOrder.orderNumber || 'LTN-????').replace(/^#/, '');
    const custName = String(rawOrder.customer_name || rawOrder.customerName || 'Pelanggan');
    const custPhone = String(rawOrder.customer_phone || rawOrder.customerPhone || '');
    const outName = String(rawOrder.outlet_name || rawOrder.outletName || adminOutletName || 'Leton Coffee');
    const outId = String(rawOrder.outlet_id || rawOrder.outletId || adminOutletId || '');
    const address = String(rawOrder.delivery_address || rawOrder.deliveryAddress || '');
    const dist = rawOrder.delivery_distance ?? rawOrder.deliveryDistance;
    const fee = rawOrder.delivery_fee ?? rawOrder.deliveryFee;
    const total = Number(rawOrder.total_amount || rawOrder.totalAmount || 0);
    const oStatus = String(rawOrder.order_status || rawOrder.orderStatus || 'NEW');
    const pStatus = String(rawOrder.payment_status || rawOrder.paymentStatus || 'PAID');
    const created = String(rawOrder.created_at || rawOrder.createdAt || new Date().toISOString());

    const notifItem: CourierNotificationItem = {
      id: `notif-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      orderId: oId,
      orderNumber: orderNum,
      customerName: custName,
      customerPhone: custPhone,
      outletName: outName,
      outletId: outId,
      deliveryAddress: address,
      deliveryDistance: typeof dist === 'number' ? dist : undefined,
      deliveryFee: typeof fee === 'number' ? fee : undefined,
      totalAmount: total,
      orderStatus: oStatus,
      paymentStatus: pStatus,
      createdAt: created,
      receivedAt: new Date().toISOString(),
      isRead: false,
    };

    setNotifications((prev) => [notifItem, ...prev.filter((n) => n.orderId !== oId)]);
    setDeliveryToast(notifItem);
    playDeliveryChime(soundMuted);
  }, [adminOutletId, adminOutletName, notifiedIdsStorageKey, soundMuted]);

  const loadWalletSummary = useCallback(async () => {
    try {
      const data = await fetchCourierWalletDashboard();
      setWalletSummary(data.wallet);
    } catch {}
  }, []);

  const loadOrders = useCallback(async (isSilent = false) => {
    if (!isSilent) setRefreshing(true);
    try {
      const fetchedOrders = await fetchCourierDeliveryOrders(adminOutletId);
      const safeOrders = Array.isArray(fetchedOrders) ? fetchedOrders : [];
      setOrders(safeOrders);
      setErrorMsg(null);
      loadWalletSummary();

      // Check freshly loaded delivery orders for unnotified new orders
      safeOrders.forEach((ord) => {
        processNewDeliveryOrderNotification(ord);
      });
    } catch (err: any) {
      setErrorMsg(err?.message || 'Kesalahan jaringan saat memuat pesanan.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminOutletId, loadWalletSummary, processNewDeliveryOrderNotification]);

  const processNotifRef = useRef(processNewDeliveryOrderNotification);
  const loadOrdersRef = useRef(loadOrders);

  useEffect(() => {
    processNotifRef.current = processNewDeliveryOrderNotification;
  }, [processNewDeliveryOrderNotification]);

  useEffect(() => {
    loadOrdersRef.current = loadOrders;
  }, [loadOrders]);

  // Initial load and fail-safe background sync (30s interval fallback)
  useEffect(() => {
    if (loadOrdersRef.current) {
      loadOrdersRef.current(false);
    }
    const interval = setInterval(() => {
      if (loadOrdersRef.current) {
        loadOrdersRef.current(true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [adminOutletId]);

  // Realtime subscription scoped strictly to the courier's outlet
  useEffect(() => {
    if (!supabase || !adminOutletId) return;

    const normOutlet = adminOutletId.toLowerCase().trim();
    const channelFilter = normOutlet === 'all'
      ? undefined
      : `outlet_id=eq.${adminOutletId}`;

    const channelName = `courier-realtime-${normOutlet}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          ...(channelFilter ? { filter: channelFilter } : {}),
        },
        (payload: any) => {
          if (payload.new) {
            processNotifRef.current(payload.new);
          }
          if (loadOrdersRef.current) {
            loadOrdersRef.current(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [adminOutletId]);

  const handleAction = async (order: CustomerOrder, action: CourierActionType) => {
    if (processingOrderId) return;
    setProcessingOrderId(order.id);
    setConfirmCompleteOrderId(null);

    try {
      const result = await performCourierOrderAction(order.id, action);
      if (!result.success) {
        showToast(result.error || 'Gagal memperbarui status pengantaran.', 'error');
      } else {
        const actionLabels: Record<CourierActionType, string> = {
          CLAIM: `Pesanan #${order.orderNumber} berhasil diklaim.`,
          PICK_UP: `Pesanan #${order.orderNumber} telah dijemput dari outlet.`,
          START_DELIVERY: `Pengantaran #${order.orderNumber} dimulai.`,
          COMPLETE_DELIVERY: `Pengantaran #${order.orderNumber} selesai diantar ke pelanggan!`,
        };
        showToast(actionLabels[action], 'success');
        if (action === 'CLAIM' || action === 'PICK_UP') {
          setActiveTab('my_active');
        } else if (action === 'COMPLETE_DELIVERY') {
          setActiveTab('completed');
        }
      }
      await loadOrders(true);
    } catch (err: any) {
      showToast(err?.message || 'Terjadi kesalahan saat memproses aksi.', 'error');
    } finally {
      setProcessingOrderId(null);
    }
  };

  const toggleExpand = (orderId: string) => {
    setExpandedOrderIds((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  // Categorize delivery orders strictly by Courier delivery_status (independent of Admin Outlet order_status)
  const categorized = useMemo(() => {
    const validOrders = orders.filter((o) => {
      const oType = String(o.orderType || '').toUpperCase();
      const oStatus = String(o.orderStatus || '').toUpperCase();
      const pStatus = String(o.paymentStatus || '').toUpperCase();
      return (
        oType === 'DELIVERY' &&
        !['CANCELLED', 'REJECTED'].includes(oStatus) &&
        !['WAITING PAYMENT', 'WAITING_PAYMENT', 'PAY AT STORE'].includes(pStatus)
      );
    });

    const available = validOrders.filter((o) => {
      const dStatus = normalizeDeliveryStatus(o.deliveryStatus || o.delivery_status);
      const assigned = String(o.courierUsername || o.courier_username || '').trim();
      return dStatus === 'UNASSIGNED' && !assigned;
    });

    const myActive = validOrders.filter((o) => {
      const dStatus = normalizeDeliveryStatus(o.deliveryStatus || o.delivery_status);
      const assigned = String(o.courierUsername || o.courier_username || '').trim().toLowerCase();
      return (
        ['ASSIGNED', 'PICKED_UP', 'ON_DELIVERY'].includes(dStatus) &&
        assigned === currentUsername &&
        Boolean(currentUsername)
      );
    });

    const allActive = validOrders.filter((o) => {
      const dStatus = normalizeDeliveryStatus(o.deliveryStatus || o.delivery_status);
      return ['UNASSIGNED', 'ASSIGNED', 'PICKED_UP', 'ON_DELIVERY'].includes(dStatus);
    });

    const completed = validOrders.filter((o) => {
      const dStatus = normalizeDeliveryStatus(o.deliveryStatus || o.delivery_status);
      const assigned = String(o.courierUsername || o.courier_username || '').trim().toLowerCase();
      return dStatus === 'DELIVERED' && (assigned === currentUsername || !currentUsername);
    });

    return { available, myActive, allActive, completed };
  }, [orders, currentUsername]);

  const displayedOrders = useMemo(() => {
    let source: CustomerOrder[] = [];
    switch (activeTab) {
      case 'available':
        source = categorized.available;
        break;
      case 'my_active':
        source = categorized.myActive;
        break;
      case 'all_active':
        source = categorized.allActive;
        break;
      case 'completed':
        source = categorized.completed;
        break;
      default:
        source = categorized.available;
    }

    if (!searchQuery.trim()) return source;

    const q = searchQuery.toLowerCase().trim();
    return source.filter((o) => {
      const num = String(o.orderNumber || '').toLowerCase();
      const name = String(o.customerName || '').toLowerCase();
      const phone = String(o.customerPhone || '').toLowerCase();
      const addr = String(o.deliveryAddress || o.delivery_address || '').toLowerCase();
      return num.includes(q) || name.includes(q) || phone.includes(q) || addr.includes(q);
    });
  }, [activeTab, categorized, searchQuery]);

  const buildMapsUrl = (order: CustomerOrder) => {
    const lat = order.customerLat ?? order.customer_lat;
    const lng = order.customerLng ?? order.customer_lng;
    const address = order.deliveryAddress || order.delivery_address || '';

    if (
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      Number.isFinite(lat) &&
      Number.isFinite(lng)
    ) {
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex flex-col font-sans selection:bg-amber-500 selection:text-slate-950">
      {/* Top Navbar */}
      <header className="sticky top-0 z-30 bg-[#111827]/95 backdrop-blur-md border-b border-slate-800/80 px-4 py-3.5">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-slate-950 shadow-lg shadow-amber-500/20 shrink-0">
              <Bike className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm sm:text-base font-bold text-white truncate">
                  Portal Kurir Leton
                </h1>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">
                  <ShieldCheck className="w-3 h-3" />
                  Kurir Resmi
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 truncate mt-0.5">
                <Store className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="truncate font-medium text-slate-300">
                  {adminOutletName || adminOutletId || 'Outlet Terkunci'}
                </span>
                <span>•</span>
                <span className="truncate text-slate-400">
                  {auth.username}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Audio Mute/Unmute Toggle */}
            <button
              type="button"
              onClick={toggleSoundMuted}
              title={soundMuted ? 'Suara Notifikasi: Mute (Klik untuk Mengaktifkan)' : 'Suara Notifikasi: Aktif (Klik untuk Mute)'}
              className={`p-2.5 rounded-xl border transition-colors cursor-pointer ${
                soundMuted
                  ? 'bg-slate-800/80 text-slate-500 border-slate-700/60'
                  : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
              }`}
            >
              {soundMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            {/* Realtime Bell Notifications Button with Unread Badge */}
            <button
              type="button"
              onClick={() => setIsNotifDrawerOpen(true)}
              title="Notifikasi Pengantaran Real-time"
              className="relative p-2.5 rounded-xl bg-slate-800/90 hover:bg-slate-700/80 text-amber-300 border border-amber-500/30 transition-colors cursor-pointer"
            >
              <Bell className="w-4 h-4 text-amber-300" />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full animate-bounce shadow-md shadow-rose-500/30">
                  {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => loadOrders(false)}
              disabled={refreshing}
              title="Muat Ulang Pesanan"
              className="p-2.5 rounded-xl bg-slate-800/90 hover:bg-slate-700/80 text-slate-200 border border-slate-700/70 transition-colors cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-amber-400' : ''}`} />
            </button>

            <button
              type="button"
              onClick={logout}
              title="Keluar dari Portal Kurir"
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/25 text-xs font-semibold transition-colors cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Keluar</span>
            </button>
          </div>
        </div>
      </header>

      {/* Real-time Floating Delivery Notification Toast Banner */}
      {deliveryToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 w-[94%] max-w-lg animate-slideDown">
          <div className="p-4 rounded-2xl bg-[#0F172A] border-2 border-amber-500/80 text-slate-100 shadow-2xl shadow-amber-500/20 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500 text-slate-950 text-xs font-black tracking-wide uppercase">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Pengantaran Baru!</span>
                </span>
                <span className="font-mono text-sm font-black text-amber-300">
                  #{deliveryToast.orderNumber}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setDeliveryToast(null)}
                className="p-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Pelanggan</span>
                <strong className="text-white text-sm block truncate">{deliveryToast.customerName}</strong>
                <span className="font-mono text-slate-400 block">{deliveryToast.customerPhone || '-'}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Total Pesanan</span>
                <strong className="text-amber-400 text-sm block">{formatCurrency(deliveryToast.totalAmount)}</strong>
                <span className="text-slate-400 block">{deliveryToast.outletName}</span>
              </div>
            </div>

            {deliveryToast.deliveryAddress && (
              <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-slate-300 flex items-start gap-2">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span className="line-clamp-2">{deliveryToast.deliveryAddress}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800/80">
              <button
                type="button"
                onClick={() => {
                  markNotifAsRead(deliveryToast.id);
                  setDeliveryToast(null);
                }}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors cursor-pointer"
              >
                Tandai Dibaca
              </button>
              <button
                type="button"
                onClick={() => handleSelectNotifOrder(deliveryToast)}
                className="px-4 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black flex items-center gap-1 transition-colors cursor-pointer shadow-md shadow-amber-500/20"
              >
                <span>Lihat & Proses</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Drawer Modal */}
      {isNotifDrawerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-lg bg-[#0F172A] border border-slate-800 rounded-3xl p-5 shadow-2xl flex flex-col max-h-[85vh] text-slate-100">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-amber-500/15 text-amber-400 border border-amber-500/30">
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-white">Notifikasi Pengantaran</h3>
                  <span className="text-xs text-slate-400">
                    {unreadNotifCount > 0 ? `${unreadNotifCount} notifikasi belum dibaca` : 'Semua notifikasi sudah dibaca'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsNotifDrawerOpen(false)}
                className="p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Action Controls */}
            {notifications.length > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-slate-800/80 text-xs">
                <button
                  type="button"
                  onClick={markAllNotifsAsRead}
                  className="text-amber-400 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Tandai Semua Dibaca</span>
                </button>
                <button
                  type="button"
                  onClick={clearAllNotifs}
                  className="text-rose-400 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Hapus Semua</span>
                </button>
              </div>
            )}

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto py-3 space-y-2.5 pr-1">
              {notifications.length === 0 ? (
                <div className="py-12 flex flex-col items-center justify-center text-center text-slate-500 space-y-2">
                  <Bell className="w-8 h-8 stroke-1 text-slate-600" />
                  <p className="text-xs font-medium">Belum ada notifikasi pesanan pengantaran baru.</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-3.5 rounded-2xl border transition-all flex flex-col gap-2 ${
                      notif.isRead
                        ? 'bg-slate-900/60 border-slate-800/60 opacity-80'
                        : 'bg-slate-900 border-amber-500/40 shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {!notif.isRead && (
                          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
                        )}
                        <span className="font-mono text-xs font-bold text-amber-400">
                          #{notif.orderNumber}
                        </span>
                        <span className="text-xs font-semibold text-slate-300">
                          {notif.customerName}
                        </span>
                      </div>
                      <span className="text-[11px] font-mono text-slate-500 shrink-0">
                        {new Date(notif.receivedAt).toLocaleTimeString('id-ID', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Outlet: <strong className="text-slate-200">{notif.outletName}</strong></span>
                      <span className="font-mono font-bold text-amber-300">{formatCurrency(notif.totalAmount)}</span>
                    </div>

                    {notif.deliveryAddress && (
                      <div className="text-[11px] text-slate-400 line-clamp-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                        <span className="truncate">{notif.deliveryAddress}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800/60">
                      {!notif.isRead && (
                        <button
                          type="button"
                          onClick={() => markNotifAsRead(notif.id)}
                          className="text-[11px] text-slate-400 hover:text-white px-2 py-1 rounded-lg bg-slate-800 cursor-pointer"
                        >
                          Tandai Dibaca
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleSelectNotifOrder(notif)}
                        className="text-[11px] text-slate-950 font-extrabold px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        <span>Lihat Pesanan</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Standard Toast Notification */}
      {toastMsg && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md">
          <div
            className={`px-4 py-3 rounded-xl shadow-2xl border flex items-center gap-3 text-sm font-medium ${
              toastMsg.type === 'success'
                ? 'bg-emerald-950/95 border-emerald-500/40 text-emerald-200'
                : 'bg-rose-950/95 border-rose-500/40 text-rose-200'
            }`}
          >
            {toastMsg.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
            )}
            <span className="flex-1">{toastMsg.text}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-5 space-y-4">
        {/* Courier Wallet Quick Banner */}
        <div className="rounded-2xl bg-gradient-to-r from-[#063E6B] via-[#0A5C99] to-[#0E74BC] p-4 border border-sky-400/30 shadow-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center text-white shrink-0">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-sky-200">
                Saldo Tersedia Kurir (100% Ongkir)
              </div>
              <div className="flex items-baseline gap-2.5 mt-0.5">
                <span className="text-xl sm:text-2xl font-black text-white">
                  {formatCurrency(walletSummary?.availableBalance ?? 0)}
                </span>
                {(walletSummary?.processingBalance ?? 0) > 0 && (
                  <span className="text-xs font-semibold text-amber-200">
                    • Diproses: {formatCurrency(walletSummary?.processingBalance)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setActiveTab(activeTab === 'wallet' ? 'available' : 'wallet')}
            className="px-4 py-2.5 rounded-xl bg-white text-[#0A5C99] hover:bg-blue-50 text-xs font-extrabold uppercase tracking-wider flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer shrink-0"
          >
            <Banknote className="w-4 h-4" />
            <span>{activeTab === 'wallet' ? 'Kembali ke Pesanan' : 'Dompet & Cairkan Saldo'}</span>
          </button>
        </div>

        {/* Web Push Notification Control Card (OneSignal Web SDK v16) */}
        <div className="rounded-2xl bg-[#111827] border border-slate-800 p-4 shadow-lg space-y-3.5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                pushStatus === 'active'
                  ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                  : pushStatus === 'denied'
                  ? 'bg-rose-500/15 border-rose-500/30 text-rose-400'
                  : 'bg-amber-500/15 border-amber-500/30 text-amber-400'
              }`}>
                <Radio className={`w-5 h-5 ${pushStatus === 'active' ? 'animate-pulse' : ''}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-white">Notifikasi Web Push Kurir (OneSignal v16)</h3>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${
                    pushStatus === 'active'
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                      : pushStatus === 'denied'
                      ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                      : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                  }`}>
                    {pushStatus === 'active' ? '● Aktif (Terdaftar)' : pushStatus === 'denied' ? '● Ditolak Browser' : '● Belum Aktif'}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  {pushStatus === 'active'
                    ? 'Pesanan Delivery baru akan memicu notifikasi push HP meskipun PWA ditutup atau perangkat terkunci.'
                    : pushStatus === 'denied'
                    ? 'Izin notifikasi ditolak oleh browser. Buka Pengaturan Situs Browser untuk mengizinkan notifikasi.'
                    : 'Aktifkan agar menerima pemberitahuan pesanan baru secara instant saat browser ditutup.'}
                </p>
              </div>
            </div>

            <div className="shrink-0">
              {pushStatus === 'denied' ? (
                <button
                  type="button"
                  onClick={handleTogglePushNotification}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30 transition-colors cursor-pointer"
                >
                  Coba Minta Izin Lagi
                </button>
              ) : (
                <button
                  type="button"
                  disabled={pushLoading || pushStatus === 'unsupported'}
                  onClick={handleTogglePushNotification}
                  className={`w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-extrabold flex items-center justify-center gap-2 transition-all cursor-pointer border ${
                    pushStatus === 'active'
                      ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                      : 'bg-amber-500 hover:bg-amber-400 text-slate-950 border-amber-400 shadow-lg shadow-amber-500/10'
                  } disabled:opacity-50`}
                >
                  {pushLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : pushStatus === 'active' ? (
                    <>
                      <XCircle className="w-4 h-4 text-rose-400" />
                      <span>Nonaktifkan Web Push</span>
                    </>
                  ) : (
                    <>
                      <BellRing className="w-4 h-4" />
                      <span>Aktifkan Notifikasi Web Push</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Status & Identitas Kurir Panel */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-800/80 text-[11px]">
            <div className="p-2 rounded-xl bg-slate-900/80 border border-slate-800 space-y-0.5">
              <span className="text-slate-500 block text-[10px] uppercase font-bold">Status Izin</span>
              <strong className={`font-mono text-xs block truncate ${
                permissionState === 'granted' ? 'text-emerald-400' : permissionState === 'denied' ? 'text-rose-400' : 'text-amber-400'
              }`}>
                {permissionState === 'granted' ? 'granted (Diizinkan)' : permissionState === 'denied' ? 'denied (Ditolak)' : 'default (Belum)'}
              </strong>
            </div>

            <div className="p-2 rounded-xl bg-slate-900/80 border border-slate-800 space-y-0.5">
              <span className="text-slate-500 block text-[10px] uppercase font-bold">External ID (User)</span>
              <strong className="font-mono text-xs text-amber-300 block truncate">
                {auth.username || '-'}
              </strong>
            </div>

            <div className="p-2 rounded-xl bg-slate-900/80 border border-slate-800 space-y-0.5">
              <span className="text-slate-500 block text-[10px] uppercase font-bold">Tag Outlet</span>
              <strong className="font-mono text-xs text-sky-300 block truncate">
                outlet_id: {normalizeOutletTag(adminOutletId)}
              </strong>
            </div>

            <div className="p-2 rounded-xl bg-slate-900/80 border border-slate-800 space-y-0.5">
              <span className="text-slate-500 block text-[10px] uppercase font-bold">Subscription ID</span>
              <strong className="font-mono text-[11px] text-slate-300 block truncate" title={oneSignalSubId || 'Belum terhubung'}>
                {oneSignalSubId
                  ? `${oneSignalSubId.slice(0, 6)}...${oneSignalSubId.slice(-4)}`
                  : pushStatus === 'active'
                  ? 'Aktif (Memuat ID...)'
                  : 'Belum Terhubung'}
              </strong>
            </div>
          </div>

          {/* Error Message Box */}
          {oneSignalError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-0.5">
                <strong className="font-bold text-white block">Aktivasi Notifikasi Membutuhkan Perhatian:</strong>
                <span>{oneSignalError}</span>
              </div>
            </div>
          )}

          {/* iOS PWA Guidance */}
          {isIOSDevice && !isPWAStandalone && (
            <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/30 text-sky-200 text-xs flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-sky-400 shrink-0" />
              <span>
                <strong>Perangkat iOS / iPhone:</strong> Untuk mengaktifkan Web Push di iOS, buka menu Share di Safari lalu pilih <strong>Add to Home Screen</strong> (Tambahkan ke Layar Utama).
              </span>
            </div>
          )}
        </div>


        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2.5">
          <button
            type="button"
            onClick={() => setActiveTab('available')}
            className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
              activeTab === 'available'
                ? 'bg-amber-500/15 border-amber-500/50 shadow-sm'
                : 'bg-[#111827] border-slate-800/80 hover:border-slate-700'
            }`}
          >
            <div className="text-[11px] font-medium text-slate-400">Menunggu Kurir</div>
            <div className="text-xl font-bold text-amber-400 mt-0.5">
              {categorized.available.length}
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('my_active')}
            className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
              activeTab === 'my_active'
                ? 'bg-sky-500/15 border-sky-500/50 shadow-sm'
                : 'bg-[#111827] border-slate-800/80 hover:border-slate-700'
            }`}
          >
            <div className="text-[11px] font-medium text-slate-400">Tugas Aktif Saya</div>
            <div className="text-xl font-bold text-sky-400 mt-0.5">
              {categorized.myActive.length}
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('completed')}
            className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
              activeTab === 'completed'
                ? 'bg-emerald-500/15 border-emerald-500/50 shadow-sm'
                : 'bg-[#111827] border-slate-800/80 hover:border-slate-700'
            }`}
          >
            <div className="text-[11px] font-medium text-slate-400">Selesai Diantar</div>
            <div className="text-xl font-bold text-emerald-400 mt-0.5">
              {categorized.completed.length}
            </div>
          </button>
        </div>

        {/* Filter Tabs & Search */}
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
            {[
              { id: 'available', label: 'Tersedia', count: categorized.available.length },
              { id: 'my_active', label: 'Aktif Saya', count: categorized.myActive.length },
              { id: 'all_active', label: 'Semua Aktif', count: categorized.allActive.length },
              { id: 'completed', label: 'Riwayat Selesai', count: categorized.completed.length },
              { id: 'wallet', label: 'Dompet Kurir', count: null },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as CourierTab)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap flex items-center gap-1.5 border transition-all cursor-pointer ${
                    isActive
                      ? 'bg-amber-500 text-slate-950 border-amber-400 shadow-sm'
                      : 'bg-[#111827] text-slate-300 border-slate-800 hover:bg-slate-800/70'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.count !== null && (
                    <span
                      className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                        isActive
                          ? 'bg-slate-950/20 text-slate-950 font-bold'
                          : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {activeTab !== 'wallet' && (
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari nomor order, nama pelanggan, WA, atau alamat..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#111827] border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/60"
              />
            </div>
          )}
        </div>

        {/* Error Banner */}
        {errorMsg && (
          <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs flex items-center gap-2.5">
            <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Courier Wallet View OR Order Cards List */}
        {activeTab === 'wallet' ? (
          <CourierWalletView
            courierUsername={currentUsername}
            courierDisplayName={adminOutletName ? `Kurir ${adminOutletName}` : currentUsername}
            outletId={adminOutletId || ''}
            onBackToOrders={() => setActiveTab('available')}
            onWalletUpdated={(w) => setWalletSummary(w)}
          />
        ) : loading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-400">
            <RefreshCw className="w-7 h-7 animate-spin text-amber-400" />
            <p className="text-sm">Memuat daftar pesanan pengiriman...</p>
          </div>
        ) : displayedOrders.length === 0 ? (
          <div className="py-14 px-4 rounded-2xl bg-[#111827]/70 border border-slate-800/80 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 rounded-2xl bg-slate-800/80 flex items-center justify-center text-slate-400 mb-3">
              <Package className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-semibold text-slate-200">
              Tidak ada pesanan pada tab ini
            </h3>
            <p className="text-xs text-slate-400 mt-1 max-w-xs">
              {activeTab === 'available'
                ? 'Belum ada pesanan Delivery baru yang menunggu kurir di outlet Anda.'
                : activeTab === 'my_active'
                ? 'Anda belum memiliki pengantaran aktif saat ini.'
                : 'Tidak ada data pesanan yang cocok dengan filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3.5 pb-10">
            {displayedOrders.map((order) => {
              const dStatus = normalizeDeliveryStatus(
                order.deliveryStatus || order.delivery_status
              );
              const dBadge = getDeliveryStatusBadge(dStatus);
              const kBadge = getKitchenStatusBadge(order.orderStatus);
              const assignedCourier = String(
                order.courierUsername || order.courier_username || ''
              ).trim();
              const isMine =
                assignedCourier.toLowerCase() === currentUsername && Boolean(currentUsername);
              const isExpanded = Boolean(expandedOrderIds[order.id]);
              const mapsUrl = buildMapsUrl(order);
              const waPhone = formatPhoneForWa(order.customerPhone);
              const address =
                order.deliveryAddress || order.delivery_address || 'Alamat tidak dicantumkan';
              const distanceKm = order.deliveryDistance ?? order.delivery_distance;
              const deliveryFee = order.deliveryFee ?? order.delivery_fee ?? 0;
              const isProcessing = processingOrderId === order.id;

              const waMessage = encodeURIComponent(
                `Halo Kak ${order.customerName || ''}, saya kurir LetonKu (${
                  adminOutletName || ''
                }) untuk pesanan Delivery #${order.orderNumber}.`
              );

              return (
                <div
                  key={order.id}
                  className="rounded-2xl bg-[#111827] border border-slate-800/90 overflow-hidden shadow-lg"
                >
                  {/* Card Header */}
                  <div className="px-4 py-3 bg-slate-900/70 border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-amber-400">
                        #{order.orderNumber}
                      </span>
                      <span className="text-xs text-slate-500">•</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleTimeString('id-ID', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '-'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${kBadge.bg}`}
                      >
                        {kBadge.label}
                      </span>
                      <span
                        className={`px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${dBadge.bg}`}
                      >
                        {dBadge.label}
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3.5">
                    {/* Customer & Quick Contact */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-bold text-white">
                          <User className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="truncate">{order.customerName || 'Pelanggan'}</span>
                        </div>
                        <div className="text-xs text-slate-400 font-mono">
                          {order.customerPhone || '-'}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {order.customerPhone && (
                          <a
                            href={`tel:${order.customerPhone}`}
                            className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-colors"
                            title="Telepon Pelanggan"
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}
                        {waPhone && (
                          <a
                            href={`https://wa.me/${waPhone}?text=${waMessage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-xs font-semibold transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>WhatsApp</span>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Delivery Address & Maps Box */}
                    <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 space-y-2.5">
                      <div className="flex items-start gap-2.5">
                        <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                            Alamat Pengantaran
                          </div>
                          <p className="text-xs sm:text-sm text-slate-100 mt-0.5 leading-relaxed break-words">
                            {address}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-400">
                            {typeof distanceKm === 'number' && distanceKm > 0 && (
                              <span>
                                Jarak: <strong className="text-slate-200">{distanceKm} km</strong>
                              </span>
                            )}
                            <span>
                              Ongkir:{' '}
                              <strong className="text-amber-300">
                                {formatCurrency(deliveryFee)}
                              </strong>
                            </span>
                            <span>
                              Total Bayar:{' '}
                              <strong className="text-white">
                                {formatCurrency(order.totalAmount)}
                              </strong>{' '}
                              ({order.paymentMethod} • {order.paymentStatus})
                            </span>
                          </div>
                        </div>
                      </div>

                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-2 px-3 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                        >
                          <Navigation className="w-3.5 h-3.5" />
                          <span>Buka Navigasi Google Maps</span>
                        </a>
                      )}
                    </div>

                    {/* Assigned Courier Info (if claimed) */}
                    {assignedCourier && (
                      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800/80 text-xs">
                        <span className="text-slate-400">Kurir Bertugas:</span>
                        <span
                          className={`font-semibold ${
                            isMine ? 'text-amber-300' : 'text-slate-300'
                          }`}
                        >
                          {assignedCourier} {isMine ? '(Anda)' : ''}
                        </span>
                      </div>
                    )}

                    {/* Collapsible Order Items & Customer Note */}
                    <div className="border-t border-slate-800/80 pt-2.5">
                      <button
                        type="button"
                        onClick={() => toggleExpand(order.id)}
                        className="w-full flex items-center justify-between text-xs text-slate-300 hover:text-white py-1 cursor-pointer"
                      >
                        <span className="font-medium">
                          Rincian Item Pesanan ({order.items?.length || 0} item)
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="mt-2 space-y-2 bg-slate-950/60 p-3 rounded-xl border border-slate-800/70 text-xs">
                          {Array.isArray(order.items) &&
                            order.items.map((item, idx) => {
                              const sizePrice = item.size?.price || 0;
                              const topPrice = item.topping?.price || 0;
                              const syrPrice = item.syrup?.price || 0;
                              const customsPrice = (item.customOptions || []).reduce(
                                (sum, c) => sum + (c.price || 0),
                                0
                              );
                              const computedUnit = Number(item.unitPrice ?? item.price ?? 0);
                              const lineTotal = computedUnit * (item.quantity || 1);

                              return (
                                <div
                                  key={`${order.id}-item-${idx}`}
                                  className="flex items-start justify-between gap-2 pb-2 last:pb-0 border-b border-slate-800/50 last:border-none"
                                >
                                  <div>
                                    <div className="font-semibold text-slate-200">
                                      {item.quantity}x {item.name}
                                    </div>
                                    {item.size?.name && (
                                      <div className="text-[11px] text-slate-400">
                                        Ukuran: {item.size.name}
                                      </div>
                                    )}
                                    {item.topping?.name && (
                                      <div className="text-[11px] text-slate-400">
                                        Topping: {item.topping.name}
                                      </div>
                                    )}
                                    {item.syrup?.name && (
                                      <div className="text-[11px] text-slate-400">
                                        Syrup: {item.syrup.name}
                                      </div>
                                    )}
                                    {item.customOptions && item.customOptions.length > 0 && (
                                      <div className="text-[11px] text-slate-400">
                                        Opsi:{' '}
                                        {item.customOptions
                                          .map((c) => `${c.groupName}: ${c.optionName}`)
                                          .join(', ')}
                                      </div>
                                    )}
                                    {item.note && (
                                      <div className="text-[11px] text-amber-300/90 italic mt-0.5">
                                        Catatan item: "{item.note}"
                                      </div>
                                    )}
                                  </div>
                                  <div className="font-mono text-slate-300 shrink-0">
                                    {formatCurrency(lineTotal)}
                                  </div>
                                </div>
                              );
                            })}

                          <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-slate-300">
                            <span>Biaya Layanan</span>
                            <span className="font-mono">{formatCurrency(resolvePersistedOrderServiceFee(order))}</span>
                          </div>

                          {order.customerNote && (
                            <div className="pt-2 border-t border-slate-800 text-amber-300">
                              <span className="font-semibold">Catatan Pelanggan: </span>
                              <span>"{order.customerNote}"</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Courier Sequential Action Buttons (Strictly controlled by delivery_status, independent of Admin Outlet orderStatus) */}
                    {dStatus !== 'DELIVERED' && (
                      <div className="pt-1">
                        {assignedCourier && !isMine ? (
                          <div className="w-full py-2.5 px-4 rounded-xl bg-slate-800/70 border border-slate-700/70 text-slate-400 text-xs font-medium text-center">
                            Diambil oleh kurir: <strong className="text-slate-200">{assignedCourier}</strong>
                          </div>
                        ) : dStatus === 'UNASSIGNED' || dStatus === 'ASSIGNED' ? (
                          <button
                            type="button"
                            disabled={isProcessing}
                            onClick={() => handleAction(order, 'PICK_UP')}
                            className="w-full py-3 px-4 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-md shadow-sky-500/10 transition-all cursor-pointer disabled:opacity-50"
                          >
                            {isProcessing ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Package className="w-4 h-4" />
                                <span>JEMPUT (Ambil Pesanan dari Outlet)</span>
                                <ArrowRight className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        ) : dStatus === 'PICKED_UP' ? (
                          <button
                            type="button"
                            disabled={isProcessing}
                            onClick={() => handleAction(order, 'START_DELIVERY')}
                            className="w-full py-3 px-4 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
                          >
                            {isProcessing ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Navigation className="w-4 h-4" />
                                <span>ANTAR (Mulai Pengantaran ke Pelanggan)</span>
                                <ArrowRight className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        ) : dStatus === 'ON_DELIVERY' ? (
                          confirmCompleteOrderId === order.id ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={isProcessing}
                                onClick={() => handleAction(order, 'COMPLETE_DELIVERY')}
                                className="flex-1 py-3 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
                              >
                                {isProcessing ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4" />
                                    <span>Ya, Selesaikan Pesanan</span>
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                disabled={isProcessing}
                                onClick={() => setConfirmCompleteOrderId(null)}
                                className="py-3 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold cursor-pointer"
                              >
                                Batal
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={isProcessing}
                              onClick={() => setConfirmCompleteOrderId(order.id)}
                              className="w-full py-3 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span>SELESAIKAN PESANAN (Pesanan Diterima Pelanggan)</span>
                            </button>
                          )
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};
